import { toast } from "react-toastify";
import {
    initializeExportDispatcher,
    getDownloadFileLink,
} from "../../store/slices/exportFile/exportFile";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const EXPORT_TYPE = "CAMPAIGN_CONTACTS_EXPORT";
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_TRIES = 40; // ~2 minutes

/**
 * [CAMPAIGN_CONTACTS_EXPORT]
 * Initialize a contacted-numbers CSV export for a COMPLETED campaign run, then
 * poll until the file is ready and trigger the browser download. The link is also
 * emailed to the user by the backend, so a slow/large export still arrives.
 * WHY a shared helper: the same flow is reused by all three run-report screens
 * (IVR / SMS / Email) — keeps the logic in one place instead of duplicating it.
 *
 * @param {object} args
 * @param {function} args.dispatch  redux dispatch
 * @param {"ivr"|"sms"|"email"} args.channel
 * @param {number|string} args.campaignId
 * @param {string} args.campaignUuid
 */
export async function downloadCampaignContactsCsv({
    dispatch,
    channel,
    campaignId,
    campaignUuid,
}) {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
        toast.error("User not found. Please re-login.");
        return;
    }
    if (!campaignId || !campaignUuid) {
        toast.error("Campaign details are missing.");
        return;
    }

    const payload = {
        exportType: EXPORT_TYPE,
        channel,
        campaignId,
        campaignUuid,
        user: user?.data,
    };

    // 1) Kick off the async export (validates permission + enqueues the job).
    const initMsg = await dispatch(initializeExportDispatcher(payload)).unwrap();
    toast.info(
        initMsg ||
            "Generating CSV… the download link will also be emailed to you."
    );

    // 2) Poll for the generated file. A "not ready" response rejects the thunk,
    //    so we swallow it and keep polling. Terminal states (ready / empty /
    //    failed) all stop the loop.
    for (let i = 0; i < MAX_POLL_TRIES; i++) {
        await sleep(POLL_INTERVAL_MS);
        try {
            const res = await dispatch(getDownloadFileLink(payload)).unwrap();

            // Terminal: export failed on the server → stop.
            if (res && res.status === "failed") {
                toast.error(res.message || "Export failed. Please try again.");
                return;
            }

            // Terminal: file ready → download.
            if (res && res.filePath) {
                const link = document.createElement("a");
                link.href = res.filePath;
                link.download = `${campaignUuid}_contacts.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success(
                    "CSV downloaded. A copy of the link was also emailed to you."
                );
                return;
            }

            // Terminal: completed, but the run contacted nobody.
            if (res && res.filePath === null) {
                toast.warn("No contacted numbers found for this campaign run.");
                return;
            }
        } catch (err) {
            // not ready yet — keep polling
        }
    }

    toast.error(
        "CSV is taking longer than expected. The download link will be emailed to you once ready."
    );
}
